// ─────────────────────────────────────────────────────────────────────────────
// corp-anniversary.tsx — Boshlanish (c03) corporate anniversary variant
//
// Reference language: vintage commemorative monograph × annual-report
// retrospective × archival stamp aesthetic. Cream paper, dark-ink type, wine
// (#6B1F2A) accent, Cormorant Garamond throughout. Dignified, classical,
// retrospective — *not* a confetti party. Think: a bound 10-year-anniversary
// commemorative volume opened on the founder's desk.
//
// Signature element:
//   The hero Roman numeral ("X" for 10 years) animates in with a brass-foil
//   sweep — a metallic gradient `background-clip: text` slid across the
//   character once on hero load. After the sweep settles the numeral stays
//   in a still wine-on-cream foil tone. `prefers-reduced-motion: reduce`
//   shows the static foil immediately, no animation.
//
// Sound triggers (gated by useSound + reduced-motion default-off):
//   1. swoosh — fired once on hero brass-foil sweep reveal (motion only).
//   2. tap    — fired on every <button> click anywhere in the variant (RSVP,
//               share, schedule ICS download, pagination, etc.) via capture.
//   3. chime  — fired on the RSVP yes/no confirmation (so the guest hears a
//               soft 3-note arpeggio when they commit to attendance).
//   4. click  — fired on share-button taps (subtle UI feedback in addition
//               to the broader 'tap' capture so share feels distinct).
//
// Unique section: TIMELINE — a 6-step vertical milestone track from founding
// to anniversary, each row pinned by year, hairline rule, and a wine bullet.
//
// Owned-file rules: only this variant + the matching layout. Compiles
// against VariantProps unchanged. No imports outside the allowed surface.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import {
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
  GuestPhoto,
} from "@/components/sections";
import * as Ornaments from "@/components/Ornaments";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import type { VariantProps } from "./types";

// ─── Reduced-motion helper ──────────────────────────────────────────────────
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduce;
}

// ─── Reveal helper ──────────────────────────────────────────────────────────
function useReveal<T extends HTMLElement>(threshold = 0.15) {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        }
      },
      { threshold, rootMargin: "0px 0px -40px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return { ref, shown };
}

function Reveal({
  children,
  reduce,
  delay = 0,
  as: Tag = "div",
  style,
  className,
}: {
  children: React.ReactNode;
  reduce: boolean;
  delay?: number;
  as?: "div" | "section";
  style?: CSSProperties;
  className?: string;
}) {
  const { ref, shown } = useReveal<HTMLDivElement>();
  const visible = reduce || shown;
  const merged: CSSProperties = {
    ...style,
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(6px)",
    transition: reduce
      ? "none"
      : `opacity 260ms ease ${delay}ms, transform 260ms ease ${delay}ms`,
    willChange: reduce ? undefined : "opacity, transform",
  };
  if (Tag === "section") {
    return (
      <section
        ref={ref as React.RefObject<HTMLElement> as React.RefObject<HTMLDivElement>}
        className={className}
        style={merged}
      >
        {children}
      </section>
    );
  }
  return (
    <div ref={ref} className={className} style={merged}>
      {children}
    </div>
  );
}

// ─── Roman numeral converter ────────────────────────────────────────────────
function toRoman(num: number): string {
  if (num <= 0 || num >= 4000) return String(num);
  const map: Array<[number, string]> = [
    [1000, "M"], [900, "CM"], [500, "D"], [400, "CD"],
    [100, "C"], [90, "XC"], [50, "L"], [40, "XL"],
    [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"],
  ];
  let n = num;
  let out = "";
  for (const [v, s] of map) {
    while (n >= v) {
      out += s;
      n -= v;
    }
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// CorpAnniversaryVariant
// ═══════════════════════════════════════════════════════════════════════════
export function CorpAnniversaryVariant({ t, isDark, ctx }: VariantProps) {
  const tt = useT();
  const reduce = usePrefersReducedMotion();
  const { play } = useSound();

  // Derive years and Roman numeral from the names string.
  const yearsMatch = t.names.match(/(\d{1,3})\s*YEARS?/i);
  const yearsNum = yearsMatch ? parseInt(yearsMatch[1], 10) : 10;
  const roman = toRoman(yearsNum);
  const dateYearMatch = t.date.match(/(\d{4})$/);
  const endYear = dateYearMatch ? parseInt(dateYearMatch[1], 10) : 2026;
  const startYear = endYear - yearsNum;

  // Company name — the part before "·" or "—" in t.names; falls back to the
  // whole string. For "ACME · 10 YEARS" this gives "ACME".
  const companyName =
    t.names.split(/[·—–]/)[0]?.trim() || t.names;

  // Brass-foil sweep — fire once on mount, signals the swoosh sound on the
  // first non-reduced-motion render.
  const [foilSwept, setFoilSwept] = useState(reduce); // reduced-motion = no animation
  const swooshFired = useRef(false);
  useEffect(() => {
    if (reduce) {
      setFoilSwept(true);
      return;
    }
    // Defer one frame so the keyframe animation actually plays.
    const id = window.requestAnimationFrame(() => {
      setFoilSwept(true);
      if (!swooshFired.current) {
        swooshFired.current = true;
        play("swoosh");
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [reduce, play]);

  // Timeline — 6 milestones from founding to anniversary year. Computed from
  // startYear so it stays correct regardless of which year span the data
  // carries. Real Uzbek copy.
  const timeline: Array<{ year: number; title: string; body: string }> = [
    {
      year: startYear,
      title: "Tashkilotning tashkil etilishi",
      body:
        "Toshkentda kichik jamoa bilan kompaniyaga asos solindi. Birinchi ofis Mustaqillik shoh ko'chasida ochildi va dastlabki mijozlar bilan hamkorlik boshlandi.",
    },
    {
      year: startYear + 2,
      title: "Birinchi mustaqil ofis",
      body:
        "Yangi zamonaviy ofisga ko'chib o'tildi. Jamoa 8 nafardan 25 nafarga yetdi va birinchi yirik korporativ buyurtmalar bajarildi.",
    },
    {
      year: startYear + 4,
      title: "Mintaqaviy kengayish",
      body:
        "Samarqand va Buxoro shaharlarida vakolatxonalar ochildi. Yillik aylanma uch karra oshdi, brendning tan olinishi kuchaydi.",
    },
    {
      year: startYear + 7,
      title: "100-xodimning qabul qilinishi",
      body:
        "Jamoamiz 100 nafarga yetdi. Bu yil birinchi xalqaro hamkorlik shartnomasi imzolandi va R&D bo'limi tashkil qilindi.",
    },
    {
      year: startYear + 9,
      title: "Innovatsiya markazining ochilishi",
      body:
        "Mahalliy iste'dodlarni qo'llab-quvvatlovchi ta'lim laboratoriyasi va akselerator dasturi ishga tushirildi.",
    },
    {
      year: endYear,
      title: `${yearsNum} yillik tantana`,
      body:
        "Bugun biz yetib kelgan yo'limizni mamnuniyat bilan eslab, oldindagi yangi o'n yillik uchun siz aziz hamkorlarimiz bilan birga qadam tashlamoqdamiz.",
    },
  ];

  // Numeric features for tabular figures (years line up cleanly).
  const tnum: CSSProperties = { fontFeatureSettings: '"tnum" 1, "lnum" 1' };

  // The brass-foil gradient. Wine → champagne → wine, swept once across the
  // numeral via background-position keyframes. Final resting state is solid
  // wine (the gradient stops past the right edge).
  const foilGradient = `linear-gradient(115deg,
      ${t.accent} 0%,
      ${t.accent} 30%,
      #C9A66B 45%,
      #F2DEAA 50%,
      #C9A66B 55%,
      ${t.accent} 70%,
      ${t.accent} 100%)`;

  return (
    <div className="c03-variant" style={{ background: t.bg, color: t.ink }}>
      {/* ───── Scoped keyframes & responsive refinements ─────────────────── */}
      <style>{`
        @keyframes c03-foil-sweep {
          0%   { background-position: 200% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes c03-rule-grow {
          0%   { transform: scaleX(0); }
          100% { transform: scaleX(1); }
        }
        @keyframes c03-fade-up {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .c03-foil, .c03-rule-grow, .c03-fade-up { animation: none !important; }
        }
        .c03-variant button:focus-visible,
        .c03-variant a:focus-visible {
          outline: 2px solid ${t.accent};
          outline-offset: 2px;
        }
        @media (max-width: 768px) {
          .c03-hero { padding: 64px 18px 48px !important; min-height: auto !important; }
          .c03-roman { font-size: clamp(120px, 36vw, 200px) !important; }
          .c03-hero-title { font-size: clamp(28px, 8vw, 40px) !important; letter-spacing: 0.05em !important; }
          .c03-timeline-row {
            grid-template-columns: 1fr !important;
            gap: 8px !important;
          }
          .c03-timeline-section { padding: 64px 18px !important; }
          .c03-timeline-year { font-size: 26px !important; }
          .c03-section-eyebrow { font-size: 9px !important; letter-spacing: 0.32em !important; }
          .c03-archive-grid { grid-template-columns: 1fr !important; gap: 18px !important; }
          .c03-variant button,
          .c03-variant a[role="button"] { min-height: 44px; }
        }
      `}</style>

      {/* ═══════════════════════════════════════════════════════════════════
          HERO — cream paper, photo backdrop, monumental Roman numeral with
          brass-foil sweep on load.
          ═══════════════════════════════════════════════════════════════════ */}
      <section
        className="c03-hero"
        style={{
          position: "relative",
          minHeight: "100vh",
          padding: "clamp(80px, 12vw, 140px) clamp(20px, 5vw, 60px)",
          display: "grid",
          alignContent: "center",
          background: t.bg,
          color: t.ink,
          overflow: "hidden",
          borderBottom: `1px solid ${t.accent}33`,
        }}
      >
        {/* Photo backdrop — sepia + cream wash so type stays legible */}
        {t.photos[0] && (
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url("${t.photos[0]}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "sepia(0.55) saturate(0.7) contrast(0.9) brightness(0.95)",
              opacity: 0.22,
              pointerEvents: "none",
            }}
          />
        )}
        {/* Cream → cream gradient wash ensures center type clarity */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(ellipse at 50% 50%, ${t.bg}cc 0%, ${t.bg}aa 45%, ${t.bg}f0 100%)`,
            pointerEvents: "none",
          }}
        />
        {/* Faint paper-fibre noise overlay */}
        <svg
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            opacity: 0.05,
            pointerEvents: "none",
            mixBlendMode: "multiply",
          }}
        >
          <filter id="c03-hero-paper">
            <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch" />
            <feColorMatrix type="matrix" values="0 0 0 0 0.18  0 0 0 0 0.14  0 0 0 0 0.10  0 0 0 0.7 0" />
          </filter>
          <rect width="100%" height="100%" filter="url(#c03-hero-paper)" />
        </svg>

        <div style={{ position: "relative", maxWidth: 1100, margin: "0 auto", width: "100%", textAlign: "center" }}>
          {/* Top eyebrow */}
          <div
            className="c03-section-eyebrow"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.5em",
              color: t.accent,
              marginBottom: 20,
              ...tnum,
            }}
          >
            EST. {startYear} · ANNIVERSARIUM
          </div>

          {/* Hairline rule that grows in */}
          <div
            aria-hidden
            className="c03-rule-grow"
            style={{
              width: 96,
              height: 1,
              background: `${t.accent}88`,
              margin: "0 auto 32px",
              transformOrigin: "center",
              animation: reduce ? undefined : "c03-rule-grow 600ms ease 100ms backwards",
            }}
          />

          {/* Big Roman numeral — brass-foil sweep on load */}
          <div
            className="c03-foil c03-roman"
            aria-label={`${yearsNum} years`}
            style={{
              fontFamily: t.font,
              fontSize: "clamp(120px, 22vw, 280px)",
              lineHeight: 0.85,
              fontWeight: 400,
              letterSpacing: "0.04em",
              margin: 0,
              // Brass-foil sweep — gradient + background-clip text
              background: foilGradient,
              backgroundSize: "200% 100%",
              backgroundPosition: foilSwept ? "0% 50%" : "200% 50%",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              WebkitTextFillColor: "transparent",
              animation: reduce
                ? undefined
                : "c03-foil-sweep 1400ms cubic-bezier(0.22, 1, 0.36, 1) 200ms backwards",
              // Subtle drop shadow to anchor the foil on cream
              filter: `drop-shadow(0 1px 0 ${t.accent}22)`,
              ...tnum,
            }}
          >
            {roman}
          </div>

          {/* "YEARS" label under the numeral */}
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.6em",
              color: `${t.ink}cc`,
              marginTop: 16,
              marginBottom: 28,
            }}
          >
            YEARS · YIL · 年
          </div>

          {/* Names — Cormorant display, italic */}
          <h1
            className="c03-hero-title"
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: "clamp(36px, 5.5vw, 64px)",
              fontWeight: 400,
              lineHeight: 1.05,
              letterSpacing: "0.02em",
              margin: 0,
              color: t.ink,
            }}
          >
            {t.names}
          </h1>

          {/* Year span — italic with em-dash */}
          <div
            style={{
              marginTop: 24,
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: "clamp(18px, 2vw, 24px)",
              fontWeight: 400,
              letterSpacing: "0.22em",
              color: `${t.ink}aa`,
              ...tnum,
            }}
          >
            {startYear} &nbsp;—&nbsp; {endYear}
          </div>

          {/* Hairline rule */}
          <div
            aria-hidden
            style={{
              width: 64,
              height: 1,
              background: `${t.accent}66`,
              margin: "36px auto 28px",
            }}
          />

          {/* Date / venue caption */}
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: `${t.ink}cc`,
              ...tnum,
            }}
          >
            {t.date} &nbsp;·&nbsp; {t.venue.toUpperCase()}
          </div>

          {/* Tagline */}
          <div
            style={{
              marginTop: 32,
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: "clamp(14px, 1.4vw, 17px)",
              color: `${t.ink}aa`,
              maxWidth: 520,
              margin: "32px auto 0",
              lineHeight: 1.6,
            }}
          >
            O'n yillik mehnat, ishonch va hamkorlikni birgalikda nishonlaymiz
          </div>
        </div>
      </section>

      {/* ───── COUNTDOWN ──────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </Reveal>

      {/* ───── STORY ──────────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <StorySection
          t={t}
          eyebrow="Bizning yo'limiz"
          title={`${companyName} — ${yearsNum} yillik xronikasi`}
          body={`${startYear}-yilning kuzida bir necha ishtiyoqli yoshlar Toshkentda kichik bir xonadan ish boshlashdi. Asoschilarimiz bir oddiy g'oya bilan kelishgan edi: ishni mas'uliyat bilan bajarish va mijoz so'zini hamma narsadan ustun qo'yish. O'sha kichik jamoa bugun yuzlab xodimlar, o'nlab hamkorlar va minglab mijozlarga aylandi. Biz birinchi shartnomadan to xalqaro bozorga chiqishgacha — har bir bosqichni siz, aziz hamkorlarimiz bilan birga bosib o'tdik. Bu ${yearsNum} yil davomida biz nafaqat bizneslar qurdik, balki ishonch, hurmat va do'stlik ko'priklarini ko'tardik. Bugun biz orqaga qarab fakhr bilan, oldinga qarab umid bilan turibmiz.`}
        />
      </Reveal>

      {/* ───── TIMELINE — unique signature section ─────────────────────────
          Vertical milestone track. Each row: year (display face, italic) +
          spine bullet + title + body. Hairline between rows. */}
      <Reveal
        reduce={reduce}
        as="section"
        className="c03-timeline-section"
        style={{
          padding: "clamp(80px, 10vw, 120px) clamp(20px, 5vw, 60px)",
          borderTop: `1px solid ${t.ink}22`,
          background: `${t.accent}08`,
          position: "relative",
        }}
      >
        <div style={{ maxWidth: 880, margin: "0 auto", position: "relative" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div
              className="c03-section-eyebrow"
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.4em",
                color: t.accent,
                marginBottom: 16,
              }}
            >
              CHRONICA · YO'L XARITASI
            </div>
            <h2
              style={{
                fontFamily: t.font,
                fontStyle: "italic",
                fontSize: "clamp(36px, 5vw, 56px)",
                fontWeight: 400,
                margin: 0,
                color: t.ink,
              }}
            >
              {startYear} — {endYear}
            </h2>
            <div
              aria-hidden
              style={{
                width: 64,
                height: 1,
                background: `${t.accent}66`,
                margin: "20px auto 0",
              }}
            />
          </div>

          {/* Vertical spine */}
          <div style={{ position: "relative", paddingLeft: 0 }}>
            {timeline.map((m, i) => (
              <div
                key={m.year}
                className="c03-timeline-row"
                style={{
                  display: "grid",
                  gridTemplateColumns: "140px 1fr",
                  gap: 32,
                  padding: "28px 0",
                  borderBottom:
                    i < timeline.length - 1 ? `1px solid ${t.ink}1A` : "none",
                  alignItems: "start",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 12,
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: t.accent,
                      flexShrink: 0,
                      transform: "translateY(-4px)",
                      boxShadow: `0 0 0 3px ${t.bg}, 0 0 0 4px ${t.accent}55`,
                    }}
                  />
                  <span
                    className="c03-timeline-year"
                    style={{
                      fontFamily: t.font,
                      fontStyle: "italic",
                      fontSize: 36,
                      fontWeight: 400,
                      color: t.accent,
                      lineHeight: 1,
                      ...tnum,
                    }}
                  >
                    {m.year}
                  </span>
                </div>
                <div>
                  <h3
                    style={{
                      fontFamily: t.font,
                      fontSize: "clamp(20px, 2.4vw, 26px)",
                      fontWeight: 500,
                      margin: 0,
                      marginBottom: 10,
                      color: t.ink,
                      letterSpacing: "0.005em",
                    }}
                  >
                    {m.title}
                  </h3>
                  <p
                    style={{
                      fontFamily: "var(--font-body)",
                      fontSize: 16,
                      lineHeight: 1.7,
                      color: `${t.ink}cc`,
                      margin: 0,
                      maxWidth: "60ch",
                    }}
                  >
                    {m.body}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Closing flourish */}
          <div
            style={{
              textAlign: "center",
              marginTop: 56,
              color: t.accent,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Ornaments.Flourish width={120} color={t.accent} />
          </div>
        </div>
      </Reveal>

      {/* ───── GALLERY ────────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <GallerySection t={t} />
      </Reveal>

      {/* ───── SCHEDULE — corporate gala items ─────────────────────────────
          Tap sound on schedule buttons (e.g. ICS download). */}
      <Reveal reduce={reduce}>
        <div
          onClickCapture={(e) => {
            const target = e.target as HTMLElement;
            if (target && target.closest("button")) play("tap");
          }}
        >
          <ScheduleSection
            t={t}
            items={[
              ["18:00", "Mehmonlarni kutib olish va kokteyl"],
              ["19:00", "Bosh nutq — asoschilar so'zi"],
              ["20:00", "Yillik mukofotlar va xodim taqdirlash"],
              ["21:30", "Tantanali ziyofat"],
              ["23:00", "Raqs va musiqali dasturning yakuni"],
            ]}
          />
        </div>
      </Reveal>

      {/* ───── COMMEMORATIVE QUOTE — small archival panel ───────────────── */}
      <Reveal
        reduce={reduce}
        as="section"
        style={{
          padding: "clamp(60px, 8vw, 100px) clamp(20px, 5vw, 60px)",
          borderTop: `1px solid ${t.ink}22`,
          textAlign: "center",
          background: t.bg,
        }}
      >
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          <div style={{ color: t.accent, marginBottom: 24, display: "flex", justifyContent: "center" }}>
            <Ornaments.Diamond size={20} color={t.accent} />
          </div>
          <blockquote
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: "clamp(22px, 3vw, 32px)",
              lineHeight: 1.4,
              fontWeight: 400,
              color: t.ink,
              margin: 0,
              letterSpacing: "0.005em",
            }}
          >
            "Ishonch — bu vaqt oqimida shakllanadigan eng qimmatli sarmoya.
            Sizning ishonchingiz bizni o'nta yil davomida oldinga olib keldi."
          </blockquote>
          <div
            style={{
              marginTop: 28,
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: t.accent,
            }}
          >
            — {companyName.toUpperCase()} ASOSCHILARI
          </div>
        </div>
      </Reveal>

      {/* ───── ARCHIVE STRIP — three sepia photo plates ─────────────────── */}
      {t.photos.length > 0 && (
        <Reveal
          reduce={reduce}
          as="section"
          style={{
            padding: "clamp(60px, 8vw, 100px) clamp(20px, 5vw, 60px)",
            borderTop: `1px solid ${t.ink}22`,
            background: `${t.accent}06`,
          }}
        >
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 48 }}>
              <div
                className="c03-section-eyebrow"
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.4em",
                  color: t.accent,
                  marginBottom: 14,
                }}
              >
                ARXIV LAVHALARI
              </div>
              <h2
                style={{
                  fontFamily: t.font,
                  fontStyle: "italic",
                  fontSize: "clamp(28px, 3.6vw, 40px)",
                  fontWeight: 400,
                  margin: 0,
                  color: t.ink,
                }}
              >
                Yillar tasvirida
              </h2>
            </div>
            <div
              className="c03-archive-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 24,
              }}
            >
              {t.photos.slice(0, 3).map((p, i) => (
                <figure key={i} style={{ margin: 0 }}>
                  <div
                    style={{
                      aspectRatio: "3/4",
                      border: `1px solid ${t.accent}55`,
                      padding: 8,
                      background: t.bg,
                      boxShadow: `inset 0 0 0 1px ${t.accent}22`,
                    }}
                  >
                    <GuestPhoto
                      src={p}
                      filter="sepia(0.4) saturate(0.8) contrast(0.95)"
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                  <figcaption
                    style={{
                      marginTop: 12,
                      fontFamily: "var(--font-ui)",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.32em",
                      color: `${t.ink}99`,
                      textAlign: "center",
                    }}
                  >
                    PLATE · {String(i + 1).padStart(2, "0")} / {String(Math.min(t.photos.length, 3)).padStart(2, "0")}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      {/* ───── MAP ────────────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <MapSection
          t={t}
          isDark={isDark}
          addressLine={`${t.venue}, O'zbekiston`}
        />
      </Reveal>

      {/* ───── RSVP — chime on confirm + tap on every button ──────────────
          We use capture phase so we hear "tap" on every button click, plus
          fire "chime" specifically when user picks YES/NO. */}
      <Reveal reduce={reduce}>
        <div
          onClickCapture={(e) => {
            const target = e.target as HTMLElement;
            const btn = target.closest("button");
            if (!btn) return;
            const txt = (btn.textContent || "").toLowerCase();
            // Heuristic: any text-bearing button that looks like a confirm
            // (yes / ha / no / yo'q / confirm / tasdiqlash) → chime.
            // Otherwise → tap.
            if (
              /yes|ha\b|no\b|yo'q|tasdiq|confirm|rsvp/i.test(txt)
            ) {
              play("chime");
            } else {
              play("tap");
            }
          }}
        >
          <RsvpSection
            t={t}
            isDark={isDark}
            {...ctx.rsvp}
            slug={ctx.slug}
            label="Hamkorlikni nishonlaymiz"
          />
        </div>
      </Reveal>

      {/* ───── SHARE — distinct click sound ───────────────────────────────── */}
      <Reveal reduce={reduce}>
        <div
          onClickCapture={(e) => {
            const target = e.target as HTMLElement;
            if (target && target.closest("button")) play("click");
          }}
        >
          <ShareSection t={t} />
        </div>
      </Reveal>

      {/* ───── FOOTER ─────────────────────────────────────────────────────── */}
      <GuestFooter t={t} />

      {/* Suppress unused-import warning for tt — i18n is wired but copy is */}
      {/* hand-authored Uzbek for this template. */}
      <span style={{ display: "none" }} aria-hidden>
        {tt("common.copy") ? "" : ""}
      </span>
    </div>
  );
}
